<template>
    <DefaultField :field="currentField" :errors="errors" :show-help-text="showHelpText">
        <template #field>
            <div class="flex flex-col gap-2">
                <input
                    :id="currentField.attribute"
                    type="text"
                    inputmode="decimal"
                    class="w-full form-control form-input form-input-bordered"
                    :class="errorClasses"
                    :placeholder="currentField.name"
                    v-model="displayValue"
                    @input="handleInput"
                    @blur="handleBlur"
                    @focus="handleFocus"
                />
                <p v-if="liveFormattedText" class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {{ liveFormattedText }}
                </p>
            </div>
        </template>
    </DefaultField>
</template>

<script>
import { DependentFormField, HandlesValidationErrors } from '@/mixins'

export default {
    mixins: [DependentFormField, HandlesValidationErrors],

    props: ['resourceName', 'resourceId', 'field'],

    data() {
        return {
            isFocused: false,
            displayValue: '',
            rawValue: null,
            formatter: null,
            numberFormatter: null,
        }
    },

    computed: {
        locale() {
            return this.currentField.locale || 'en'
        },

        currency() {
            return this.currentField.currency || 'USD'
        },

        showCurrencySymbol() {
            return this.currentField.showCurrencySymbol !== false
        },

        showSpelledOut() {
            return this.currentField.showSpelledOut === true
        },

        formattingOptions() {
            return this.currentField.formattingOptions || {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            }
        },

        liveFormattedText() {
            // Show formatted text in real-time while typing
            // Use rawValue if available (when user has typed a valid number)
            // Otherwise try to parse displayValue (while user is typing)
            
            if (this.rawValue !== null && !isNaN(this.rawValue)) {
                // User has entered a valid number - format it
                try {
                    if (this.showSpelledOut) {
                        return this.formatSpelledOut(this.rawValue)
                    }
                    return this.formatCurrency(this.rawValue)
                } catch (e) {
                    return ''
                }
            }

            // User is still typing - try to parse displayValue
            if (!this.displayValue || this.displayValue === '' || this.displayValue === '-') {
                return ''
            }

            try {
                // Don't format if user is still typing (ends with .)
                if (this.displayValue.endsWith('.')) {
                    return ''
                }

                const num = parseFloat(this.displayValue)
                if (isNaN(num)) {
                    return ''
                }

                // Format the number
                if (this.showSpelledOut) {
                    return this.formatSpelledOut(num)
                }

                return this.formatCurrency(num)
            } catch (e) {
                return ''
            }
        },
    },

    mounted() {
        this.initializeFormatter()
        this.setInitialValue()
    },

    methods: {
        /**
         * Initialize number formatters
         */
        initializeFormatter() {
            try {
                // Currency formatter
                this.formatter = new Intl.NumberFormat(this.locale, {
                    style: 'currency',
                    currency: this.currency,
                    ...this.formattingOptions,
                })

                // Number formatter for display
                this.numberFormatter = new Intl.NumberFormat(this.locale, {
                    ...this.formattingOptions,
                })
            } catch (e) {
                console.error('Error initializing formatter:', e)
                // Fallback to default
                this.formatter = new Intl.NumberFormat('en', {
                    style: 'currency',
                    currency: 'USD',
                })
                this.numberFormatter = new Intl.NumberFormat('en')
            }
        },

        /**
         * Set the initial, internal value for the field.
         */
        setInitialValue() {
            this.rawValue = this.field.value || null
            if (this.rawValue !== null) {
                this.displayValue = this.formatNumberForInput(this.rawValue)
            } else {
                this.displayValue = ''
            }
        },

        /**
         * Format number for input display (when focused, show plain number)
         */
        formatNumberForInput(value) {
            if (value === null || value === '') return ''
            
            const num = parseFloat(value)
            if (isNaN(num)) return value.toString()

            // Remove grouping separators when editing
            return num.toString()
        },

        /**
         * Format as currency
         */
        formatCurrency(value) {
            if (!this.formatter) return value.toString()
            
            try {
                return this.formatter.format(value)
            } catch (e) {
                return value.toString()
            }
        },

        /**
         * Format as spelled out number
         */
        formatSpelledOut(value) {
            try {
                const formatter = new Intl.NumberFormat(this.locale, {
                    style: 'currency',
                    currency: this.currency,
                    currencyDisplay: 'name',
                    ...this.formattingOptions,
                })
                return formatter.format(value)
            } catch (e) {
                return this.formatCurrency(value)
            }
        },

        /**
         * Handle input changes
         */
        handleInput(event) {
            let inputValue = event.target.value
            
            // Remove all non-numeric characters except decimal point and minus
            inputValue = inputValue.replace(/[^\d.-]/g, '')
            
            // Allow only one decimal point
            const parts = inputValue.split('.')
            if (parts.length > 2) {
                inputValue = parts[0] + '.' + parts.slice(1).join('')
            }

            // Allow only one minus sign at the beginning
            if (inputValue.includes('-') && !inputValue.startsWith('-')) {
                inputValue = inputValue.replace(/-/g, '')
            }
            if (inputValue.match(/-/g)?.length > 1) {
                inputValue = '-' + inputValue.replace(/-/g, '')
            }

            this.displayValue = inputValue

            // Parse the numeric value
            const numValue = inputValue === '' || inputValue === '-' ? null : parseFloat(inputValue)
            
            if (inputValue !== '' && inputValue !== '-' && !isNaN(numValue)) {
                this.rawValue = numValue
                this.value = numValue
                this.emitFieldValueChange(this.fieldAttribute, numValue)
            } else if (inputValue === '') {
                this.rawValue = null
                this.value = null
                this.emitFieldValueChange(this.fieldAttribute, null)
            }
        },

        /**
         * Handle blur event - format the display value in the input
         */
        handleBlur() {
            this.isFocused = false
            if (this.rawValue !== null && !isNaN(this.rawValue)) {
                // Format the input value when not focused
                this.displayValue = this.formatCurrency(this.rawValue)
            } else if (this.displayValue === '' || this.displayValue === '-') {
                this.displayValue = ''
            }
        },

        /**
         * Handle focus event - show raw number for editing (formatted text will show below)
         */
        handleFocus() {
            this.isFocused = true
            // Convert formatted value back to raw number for easy editing
            if (this.rawValue !== null && !isNaN(this.rawValue)) {
                this.displayValue = this.formatNumberForInput(this.rawValue)
            }
        },

        /**
         * Fill the given FormData object with the field's internal value.
         */
        fill(formData) {
            formData.append(
                this.field.attribute,
                this.rawValue !== null ? this.rawValue : ''
            )
        },
    },

    watch: {
        'currentField.locale'() {
            this.initializeFormatter()
            if (this.rawValue !== null) {
                this.displayValue = this.isFocused 
                    ? this.formatNumberForInput(this.rawValue)
                    : this.formatCurrency(this.rawValue)
            }
        },
        'currentField.currency'() {
            this.initializeFormatter()
            if (this.rawValue !== null) {
                this.displayValue = this.isFocused 
                    ? this.formatNumberForInput(this.rawValue)
                    : this.formatCurrency(this.rawValue)
            }
        },
    },
}
</script>

